import { useEffect, useState } from "react";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { formSurfaceSx } from "constants/pageSurfaces";
import Breadcrumbs from "components/common/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "services/api.service";
import useLocationDetail from "hooks/locations/useLocation";
import LocationForm, { locationToForm, LocationFormValues } from "./LocationForm";

const adaptLocationFormOnChange =
  (
    setValues: React.Dispatch<React.SetStateAction<LocationFormValues | null>>
  ): React.Dispatch<React.SetStateAction<LocationFormValues>> =>
  (updater) => {
    setValues((prev) => {
      if (prev === null) return null;
      return typeof updater === "function" ? updater(prev) : updater;
    });
  };

export default function LocationEdit() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { location, isLoading, isError } = useLocationDetail(uuid);
  const [values, setValues] = useState<LocationFormValues | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (location) {
      setValues(locationToForm(location));
    }
  }, [location]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!uuid || !values) return;
    setError(null);
    api
      .put(`/locations/${uuid}`, { location: values })
      .then(() => navigate("/locations"))
      .catch((err) => {
        const msg =
          err?.response?.data?.errors?.join?.(", ") ||
          err?.response?.data?.errors ||
          "Could not update location";
        setError(String(msg));
      });
  };

  if (isLoading && !location) {
    return (
      <Box display='flex' justifyContent='center' mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !location || !values) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        Location not found.
      </Alert>
    );
  }

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: 'Dashboard', to: '/dashboard' },
            { label: 'Locations', to: '/locations' },
            { label: 'Edit' },
          ]}
        />
      </Box>
      <Box sx={formSurfaceSx}>
        <Typography variant='h4' mb={2} fontWeight='bold'>
          Edit location
        </Typography>
        {error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <LocationForm
          values={values}
          onChange={adaptLocationFormOnChange(setValues)}
          onSubmit={handleSubmit}
          submitLabel='Save'
          cancelTo='/locations'
        />
      </Box>
    </div>
  );
}
